import React, { useEffect, useRef, useState } from 'react';

// Public board for the Clue murder-mystery activity. No auth — this is the
// screen projected for the whole room to watch. Polls GET /api/clue/board
// and also opens the SSE stream for instant pushes; either one alone would
// be enough (SSE is the "fast path," polling is the documented fallback),
// so this just does both and refetches on every stream event.

const API_BASE = 'https://forteindiedev.com/api/clue';
const POLL_MS = 5000;

const STATUS_COLORS = {
    idle: '#2e7d32',
    on_a_call: '#f9a825',
    muted: '#9e9e9e',
};

// Character card art. Drop the images in your frontend's public/ folder
// (public/clue-cards/scarlett.png, etc.) and these paths just work. Using a
// different filename or extension? Change the path here. If your images live
// in src/ instead, import them at the top of this file and put the imported
// values in this map. A character with no image (or an image that fails to
// load) falls back to the plain text card.
const CARD_IMAGES = {
    scarlett: '/clue-cards/scarlett.png',
    mustard: '/clue-cards/mustard.png',
    white: '/clue-cards/white.png',
    green: '/clue-cards/green.png',
    peacock: '/clue-cards/peacock.png',
    plum: '/clue-cards/plum.png',
};

// +15551230001 -> (555) 123-0001. Anything that isn't a US +1 number is
// shown as-is.
function formatPhone(phone) {
    const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(phone || '');
    return m ? `(${m[1]}) ${m[2]}-${m[3]}` : phone || '';
}

function CastCard({ character }) {
    const [imageFailed, setImageFailed] = useState(false);
    const src = CARD_IMAGES[character.id];
    const showImage = src && !imageFailed;
    const color = STATUS_COLORS[character.status] || '#666';
    const muted = character.status === 'muted';
    const onCall = character.status === 'on_a_call';

    return (
        <div
            style={{
                border: `2px solid ${onCall ? color : '#333'}`,
                borderRadius: 12,
                overflow: 'hidden',
                background: '#1b1b1b',
                color: '#eee',
                width: 200,
                boxShadow: onCall ? `0 0 18px ${color}` : 'none',
                transition: 'box-shadow 0.3s, border-color 0.3s',
            }}
        >
            {showImage && (
                <img
                    src={src}
                    alt={character.display_name}
                    onError={() => setImageFailed(true)}
                    style={{
                        display: 'block',
                        width: '100%',
                        height: 'auto',
                        filter: muted ? 'grayscale(1) brightness(0.6)' : 'none',
                        transition: 'filter 0.3s',
                    }}
                />
            )}
            <div style={{ padding: '10px 14px' }}>
                {!showImage && (
                    <>
                        <div style={{ fontWeight: 700, fontSize: 18 }}>{character.display_name}</div>
                        <div style={{ fontSize: 13, color: '#aaa' }}>{character.job_title}</div>
                    </>
                )}
                <div style={{ marginTop: showImage ? 0 : 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span
                        style={{
                            display: 'inline-block',
                            width: 10,
                            height: 10,
                            borderRadius: '50%',
                            background: color,
                        }}
                    />
                    <span style={{ fontSize: 13, textTransform: 'capitalize' }}>
                        {character.status.replace('_', ' ')}
                    </span>
                </div>
                {character.phone && (
                    <div style={{ marginTop: 6, fontSize: 15, fontWeight: 600, letterSpacing: 0.5, color: '#ddd' }}>
                        {formatPhone(character.phone)}
                    </div>
                )}
            </div>
        </div>
    );
}

export default function ClueBoard() {
    const [board, setBoard] = useState(null);
    const [error, setError] = useState(null);
    const esRef = useRef(null);

    async function fetchBoard() {
        try {
            const res = await fetch(`${API_BASE}/board`);
            if (!res.ok) throw new Error(`board fetch failed: ${res.status}`);
            const data = await res.json();
            setBoard(data);
            setError(null);
        } catch (err) {
            setError(err.message);
        }
    }

    useEffect(() => {
        fetchBoard();
        const pollId = setInterval(fetchBoard, POLL_MS);

        try {
            const es = new EventSource(`${API_BASE}/stream`);
            es.onmessage = fetchBoard;
            ['round_changed', 'event_appended', 'character_status', 'beat_updated', 'accusation_count'].forEach((evt) => {
                es.addEventListener(evt, fetchBoard);
            });
            es.onerror = () => {
                // Let the poll interval carry things if the stream drops —
                // no need to surface this to the room.
            };
            esRef.current = es;
        } catch (err) {
            // EventSource not available for some reason — polling alone covers it.
        }

        return () => {
            clearInterval(pollId);
            if (esRef.current) esRef.current.close();
        };
    }, []);

    if (error && !board) {
        return (
            <div style={{ padding: 40, color: '#fff', background: '#111', minHeight: '100vh' }}>
                Couldn't reach the game server: {error}
            </div>
        );
    }
    if (!board) {
        return (
            <div style={{ padding: 40, color: '#fff', background: '#111', minHeight: '100vh' }}>
                Loading...
            </div>
        );
    }

    return (
        <div style={{ background: '#111', color: '#eee', minHeight: '100vh', padding: '24px 32px', fontFamily: 'system-ui, sans-serif' }}>
            <h1 style={{ margin: 0 }}>Who Killed Pippin Took?</h1>
            <div style={{ fontSize: 18, color: '#ccc', marginTop: 4 }}>
                {board.phase === 'lobby' && 'Waiting to start...'}
                {board.phase === 'open' && board.round && (
                    <>Round: <strong>{board.round.name}</strong> — {board.round.purpose}</>
                )}
                {board.phase === 'closed' && 'Round closed — comparing notes.'}
                {board.phase === 'deliberation' && 'Deliberation — submit your accusation!'}
                {board.phase === 'revealed' && (
                    <span style={{ color: '#ff5252', fontWeight: 700 }}>
                        The killer was {board.cast.find((c) => c.id === board.killer_revealed)?.display_name || board.killer_revealed}!
                    </span>
                )}
            </div>

            <h2 style={{ marginTop: 32 }}>Employees</h2>
            <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                {board.cast.map((c) => (
                    <CastCard key={c.id} character={c} />
                ))}
            </div>

            <h2 style={{ marginTop: 32 }}>Corkboard</h2>
            {board.corkboard.length === 0 ? (
                <div style={{ color: '#888' }}>Nothing pinned up yet.</div>
            ) : (
                <ul>
                    {board.corkboard.map((f) => (
                        <li key={f.id} style={{ marginBottom: 6 }}>{f.wording}</li>
                    ))}
                </ul>
            )}

            <h2 style={{ marginTop: 32 }}>Timeline</h2>
            <div style={{ maxHeight: 300, overflowY: 'auto', border: '1px solid #333', borderRadius: 8, padding: 12 }}>
                {board.timeline.length === 0 ? (
                    <div style={{ color: '#888' }}>Nothing's happened yet.</div>
                ) : (
                    board.timeline.slice().reverse().map((e) => (
                        <div key={e.id} style={{ fontSize: 14, marginBottom: 6, color: '#ccc' }}>
                            <span style={{ color: '#888' }}>[R{e.round_index}]</span> {e.text}
                        </div>
                    ))
                )}
            </div>

            <div style={{ marginTop: 24, color: '#888', fontSize: 14 }}>
                Accusations submitted: {board.accusation_count}
            </div>
        </div>
    );
}